import styles from './FileUploadConfigTab.module.css';
import ConnectorSelect from '../../../../../ui/ConnectorSelect/ConnectorSelect';

export interface FileUploadConfigValues {
  uploadEnabled: boolean;
  uploadAllowedTypes: string[];
  uploadMaxSizeMb: number;
  uploadS3ConnectionId: string;
}

interface Connector {
  id: string;
  connectionName: string;
  environment?: string;
  connectionTypeId?: number;
}

interface Props {
  config: FileUploadConfigValues;
  onChange: (partial: Partial<FileUploadConfigValues>) => void;
  connectors?: Connector[];
}

interface FileTypeOption {
  label: string;
  mimeTypes: string[];
}

const FILE_TYPE_OPTIONS: FileTypeOption[] = [
  { label: 'PDF', mimeTypes: ['application/pdf'] },
  { label: 'PNG', mimeTypes: ['image/png'] },
  { label: 'JPEG', mimeTypes: ['image/jpeg'] },
  { label: 'GIF', mimeTypes: ['image/gif'] },
  { label: 'Word', mimeTypes: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'] },
  { label: 'Excel', mimeTypes: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] },
  { label: 'CSV', mimeTypes: ['text/csv'] },
  { label: 'Text', mimeTypes: ['text/plain'] },
];

export default function FileUploadConfigTab({ config, onChange, connectors = [] }: Props) {
  const isOptionChecked = (option: FileTypeOption) =>
    option.mimeTypes.every(mt => config.uploadAllowedTypes.includes(mt));

  const toggleOption = (option: FileTypeOption) => {
    const checked = isOptionChecked(option);
    onChange({
      uploadAllowedTypes: checked
        ? config.uploadAllowedTypes.filter(mt => !option.mimeTypes.includes(mt))
        : [...config.uploadAllowedTypes, ...option.mimeTypes.filter(mt => !config.uploadAllowedTypes.includes(mt))],
    });
  };

  const s3Connectors = connectors.filter(c => c.connectionTypeId === 2);
  const s3Options = s3Connectors.map(c => ({
    id: c.id,
    name: c.connectionName,
    environment: c.environment,
  }));

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>File Uploads</h3>
      <div className={styles.field}>
        <label>Enable File Uploads</label>
        <div className={styles.toggleRow}>
          <button
            className={`${styles.toggleBtn} ${config.uploadEnabled ? styles.toggleBtnActive : ''}`}
            onClick={() => onChange({ uploadEnabled: !config.uploadEnabled })} type="button"
          >
            {config.uploadEnabled ? 'Enabled' : 'Disabled'}
          </button>
          <span className={styles.toggleHint}>
            {config.uploadEnabled ? 'Users can attach files in the chat' : 'Attachment icon hidden from chat'}
          </span>
        </div>
      </div>

      {config.uploadEnabled && (
        <>
          <div className={styles.field}>
            <label>Allowed File Types</label>
            <div className={styles.checkboxGroup}>
              {FILE_TYPE_OPTIONS.map(opt => (
                <label key={opt.label} className={styles.checkboxLabel}>
                  <input type="checkbox" checked={isOptionChecked(opt)} onChange={() => toggleOption(opt)} />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className={styles.field}>
            <label>Max File Size (MB)</label>
            <input type="number" value={config.uploadMaxSizeMb} min={1} max={100}
              onChange={e => onChange({ uploadMaxSizeMb: parseInt(e.target.value) || 1 })} />
          </div>
          <div className={styles.field}>
            <label>File Storage (S3 Connector)</label>
            <p className={styles.fieldHint}>
              Select an S3 connector to store uploaded files. When not set, files are stored on local disk.
            </p>
            <div className={styles.connectorRow}>
              <ConnectorSelect
                options={s3Options}
                value={config.uploadS3ConnectionId || ''}
                onChange={id => onChange({ uploadS3ConnectionId: id })}
                placeholder="Local disk (default)"
                disabled={s3Connectors.length === 0}
              />
              {config.uploadS3ConnectionId && (
                <button
                  className={styles.clearBtn}
                  onClick={() => onChange({ uploadS3ConnectionId: '' })}
                  type="button"
                >
                  Clear
                </button>
              )}
            </div>
            {s3Connectors.length === 0 && (
              <p className={styles.fieldHint}>
                No S3 connectors available. Add an S3 connection in the Connectors settings first.
              </p>
            )}
            {!config.uploadS3ConnectionId && (
              <p className={styles.s3Warning}>
                File uploads are enabled but no S3 connector is selected. Users will not be able to upload files until an S3 connector is configured.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
